import React, { useState } from 'react';
import { Upload, FileText, Type, Image, Zap, Download, AlertCircle } from 'lucide-react';
import { Document, Packer, Paragraph } from 'docx';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Set up the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

export default function Convertly() {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [wordFile, setWordFile] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageWidth, setImageWidth] = useState('');
  const [imageHeight, setImageHeight] = useState('');
  const [imageFormat, setImageFormat] = useState('PNG');
  const [imagePreview, setImagePreview] = useState(null);
  const [keepAspectRatio, setKeepAspectRatio] = useState(true);
  const [originalImageSize, setOriginalImageSize] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [removeSpaces, setRemoveSpaces] = useState(true);
  const [fixBreaks, setFixBreaks] = useState(false);
  const [caseChange, setCaseChange] = useState('No case change');
  const [cleanedText, setCleanedText] = useState('');
  const [conversionMode, setConversionMode] = useState('pdf-to-word');
  const [converting, setConverting] = useState(false);
  const [convertedFile, setConvertedFile] = useState(null);
  const [processingImage, setProcessingImage] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [aiText, setAiText] = useState('');
  const [humanizedText, setHumanizedText] = useState('');
  const [humanizingText, setHumanizingText] = useState(false);

  // PDF to Word conversion using PDF.js + docx
  const handlePdfToWord = async () => {
    if (!pdfFile) {
      setErrorMsg('Please select a PDF file');
      return;
    }

    setConverting(true);
    setErrorMsg('');

    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let extractedText = '';
      
      // Extract text from all pages
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(' ');
        
        if (pageNum > 1) extractedText += '\n\n';
        extractedText += `--- Page ${pageNum} ---\n`;
        extractedText += pageText || '[Page content not readable]';
      }

      // Create Word document with extracted text
      const paragraphs = extractedText
        .split('\n')
        .filter(line => line.trim())
        .map(line => 
          new Paragraph({
            text: line,
            spacing: { after: 100 }
          })
        );

      const doc = new Document({
        sections: [{
          children: paragraphs.length > 0 ? paragraphs : [
            new Paragraph({
              text: 'PDF Conversion Complete - Text extracted successfully',
              spacing: { after: 100 }
            })
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      
      setConvertedFile({
        url,
        name: pdfFile.name.replace('.pdf', '.docx')
      });
      
      setErrorMsg(`✓ PDF converted to Word successfully! ${pdf.numPages} pages extracted.`);
      setConverting(false);
    } catch (error) {
      setErrorMsg(`Conversion failed: ${error.message}`);
      setConverting(false);
    }
  };

  // Word to PDF conversion using Mammoth + jsPDF + html2canvas
  const handleWordToPdf = async () => {
    if (!wordFile) {
      setErrorMsg('Please select a Word file');
      return;
    }

    setConverting(true);
    setErrorMsg('');

    try {
      // Read the Word file as ArrayBuffer
      const arrayBuffer = await wordFile.arrayBuffer();

      // Convert DOCX to HTML using Mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
      const htmlContent = result.value;

      // Create a temporary container for the HTML
      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      container.style.padding = '20px';
      container.style.fontFamily = 'Arial, sans-serif';
      container.style.lineHeight = '1.5';
      container.style.width = '800px';
      container.style.backgroundColor = 'white';
      document.body.appendChild(container);

      // Convert the HTML element to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      // Remove the temporary container
      document.body.removeChild(container);

      // Create PDF from canvas
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add images to PDF, creating new pages as needed
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      pdf.save(wordFile.name.replace(/\.(doc|docx)$/, '.pdf'));

      setErrorMsg('✓ Word file converted to PDF successfully!');
      setConverting(false);
    } catch (error) {
      setErrorMsg(`Conversion failed: ${error.message}`);
      setConverting(false);
    }
  };

  // Image processing function
  const handleImageResize = () => {
    if (!imageFile) {
      setErrorMsg('Please select an image');
      return;
    }

    if (!imageWidth || !imageHeight) {
      setErrorMsg('Please enter width and height');
      return;
    }

    setProcessingImage(true);
    setErrorMsg('');

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas size
          canvas.width = parseInt(imageWidth);
          canvas.height = parseInt(imageHeight);

          // Draw and resize image
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Convert to selected format
          const mimeTypeMap = {
            'PNG': 'image/png',
            'JPG': 'image/jpeg',
            'WebP': 'image/webp'
          };
          const mimeType = mimeTypeMap[imageFormat];

          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const ext = imageFormat.toLowerCase();
            setProcessedImage({
              url,
              name: imageFile.name.split('.')[0] + '.' + ext
            });
            setErrorMsg(`✓ Image resized to ${imageWidth}x${imageHeight}px in ${imageFormat} format!`);
            setProcessingImage(false);
          }, mimeType);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(imageFile);
    } catch (err) {
      setErrorMsg('Image processing failed: ' + err.message);
      setProcessingImage(false);
    }
  };

  // Download helper
  const downloadFile = (file) => {
    const a = document.createElement('a');
    a.href = file.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleProcessText = () => {
    let processed = textInput;
    if (removeSpaces) processed = processed.replace(/\s+/g, ' ').trim();
    if (fixBreaks) processed = processed.replace(/\n\n+/g, '\n');
    if (caseChange === 'Uppercase') processed = processed.toUpperCase();
    else if (caseChange === 'Lowercase') processed = processed.toLowerCase();
    else if (caseChange === 'Capitalize') processed = processed.charAt(0).toUpperCase() + processed.slice(1).toLowerCase();
    setCleanedText(processed);
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setTextInput(text);
    
    // Proper word counting with regex
    const words = text.trim().match(/\b[\w'-]+\b/g);
    setWordCount(words ? words.length : 0);
    setCharCount(text.length);
  };

  const handleWidthChange = (value) => {
    setImageWidth(value);
    if (keepAspectRatio && originalImageSize && value) {
      const ratio = originalImageSize.height / originalImageSize.width;
      setImageHeight(Math.round(parseInt(value) * ratio).toString());
    }
  };

  const handleHeightChange = (value) => {
    setImageHeight(value);
    if (keepAspectRatio && originalImageSize && value) {
      const ratio = originalImageSize.width / originalImageSize.height;
      setImageWidth(Math.round(parseInt(value) * ratio).toString());
    }
  };

  // AI Detection Score function
  const calculateAIScore = (text) => {
    let aiIndicators = 0;
    let totalChecks = 0;

    // Check for formal transitions (AI loves these)
    const formalTransitions = ['Additionally', 'Furthermore', 'In conclusion', 'In summary', 'Moreover', 'It is important to note', 'As mentioned earlier', 'In essence', 'Ultimately'];
    formalTransitions.forEach(transition => {
      totalChecks += 2;
      if (text.includes(transition)) aiIndicators += 2;
    });

    // Check for lack of contractions (AI avoids these)
    const contractionMissing = ['cannot', 'will not', 'do not', 'does not', 'is not', 'are not', 'have not', 'has not'];
    contractionMissing.forEach(word => {
      totalChecks += 1;
      if (text.includes(word)) aiIndicators += 1;
    });

    // Check for overly formal language
    const formalWords = ['endeavor', 'facilitate', 'implement', 'leverage', 'paradigm', 'synthesize', 'utilize', 'optimize'];
    formalWords.forEach(word => {
      totalChecks += 1.5;
      if (text.toLowerCase().includes(word)) aiIndicators += 1.5;
    });

    // Check sentence length (AI uses longer sentences)
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const avgSentenceLength = sentences.reduce((acc, s) => acc + s.split(' ').length, 0) / sentences.length;
    totalChecks += 2;
    if (avgSentenceLength > 20) aiIndicators += 2;

    // Check for passive voice (common in AI)
    const passiveVoicePattern = /\bwas\b|\bwere\b|\bbeing\b|\bbeen\b/gi;
    const passiveMatches = (text.match(passiveVoicePattern) || []).length;
    totalChecks += 1.5;
    if (passiveMatches > sentences.length * 0.3) aiIndicators += 1.5;

    // Check for repetitive structure
    const words = text.toLowerCase().split(/\s+/);
    const uniqueWords = new Set(words).size;
    const diversity = uniqueWords / words.length;
    totalChecks += 1.5;
    if (diversity < 0.4) aiIndicators += 1.5;

    // Calculate final score
    const score = Math.round((aiIndicators / totalChecks) * 100);
    return Math.min(100, Math.max(0, score));
  };

  // AI Text Humanizer function
  const handleHumanizeText = async () => {
    if (!aiText.trim()) {
      setErrorMsg('Please enter text to humanize');
      return;
    }

    setHumanizingText(true);
    setErrorMsg('');

    try {
      // Calculate AI detection score BEFORE humanization
      const aiScore = calculateAIScore(aiText);

      // Simple humanization: Add variety to sentences, make it more natural
      let result = aiText;

      // Add more natural transitions
      result = result.replace(/Additionally,/g, 'What\'s more,');
      result = result.replace(/Furthermore,/g, 'Besides,');
      result = result.replace(/In conclusion,/g, 'To sum up,');
      result = result.replace(/In summary,/g, 'All in all,');
      result = result.replace(/Moreover,/g, 'Plus,');

      // Add contractions to make it more conversational
      result = result.replace(/cannot/g, 'can\'t');
      result = result.replace(/will not/g, 'won\'t');
      result = result.replace(/do not/g, 'don\'t');
      result = result.replace(/does not/g, 'doesn\'t');
      result = result.replace(/is not/g, 'isn\'t');
      result = result.replace(/are not/g, 'aren\'t');
      result = result.replace(/have not/g, 'haven\'t');
      result = result.replace(/has not/g, 'hasn\'t');

      // Break up long sentences
      result = result.replace(/\. ([A-Z])/g, '.\n\n$1');

      // Add some varied punctuation
      result = result.replace(/\.{1}$/gm, (match, offset) => {
        const rand = Math.random();
        if (rand > 0.85) return '!';
        if (rand > 0.7) return '...';
        return '.';
      });

      // Calculate new score AFTER humanization
      const newScore = calculateAIScore(result);
      const improvement = aiScore - newScore;

      setHumanizedText(result);
      setErrorMsg(`✓ AI Detection: ${aiScore}% → ${newScore}% (${improvement}% improvement!) Text is now more human-like.`);
      setHumanizingText(false);
    } catch (error) {
      setErrorMsg(`Humanization failed: ${error.message}`);
      setHumanizingText(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'PDF ↔ Word', id: 'pdf-word' },
    { label: 'Text Cleaner', id: 'text-cleaner' },
    { label: 'Image Resizer', id: 'image-resizer' },
    { label: 'AI Humanizer', id: 'ai-humanizer' },
  ];

  // Ad placeholder component

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", margin: 0, padding: 0 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Inter:wght@400;500;600&display=swap');
        
        h1, h2, h3, h4 {
          font-family: 'Poppins', sans-serif;
        }
        
        body { margin: 0; padding: 0; }
        
        button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        
        button:active {
          transform: scale(0.98);
        }

        /* Desktop - Always show nav */
        .desktop-nav {
          display: flex !important;
        }

        .mobile-hamburger {
          display: none !important;
        }

        /* Mobile - Hide nav, show hamburger */
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          
          .mobile-hamburger {
            display: block !important;
          }

          main {
            padding: 32px 16px !important;
          }

          h1 {
            font-size: 28px !important;
          }

          h2 {
            font-size: 24px !important;
          }

          p {
            font-size: 15px !important;
          }

          textarea {
            font-size: 16px !important;
          }

          input {
            font-size: 16px !important;
          }
        }
      `}</style>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://convertly.be"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "PDF to Word Converter",
            "item": "https://convertly.be/#pdf-word"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Text Cleaner",
            "item": "https://convertly.be/#text-cleaner"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "Image Resizer",
            "item": "https://convertly.be/#image-resizer"
          },
          {
            "@type": "ListItem",
            "position": 5,
            "name": "AI Humanizer",
            "item": "https://convertly.be/#ai-humanizer"
          }
        ]
      })}
      </script>

      {/* Header */}
      <header style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button 
            onClick={() => setCurrentPage('home')}
            style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Convertly
          </button>
          
          {/* Desktop Navigation - Hidden on mobile */}
          <nav className="desktop-nav" style={{ display: 'flex', gap: '32px' }}>
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: currentPage === item.id ? '#ef4444' : '#6b7280',
                  borderBottom: currentPage === item.id ? '2px solid #ef4444' : 'none',
                  paddingBottom: currentPage === item.id ? '0' : '2px',
                  transition: 'all 0.2s ease'
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Mobile Hamburger Button - Only visible on mobile */}
          <button
            className="mobile-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '28px',
              color: '#6b7280',
              padding: '8px',
              margin: '-8px'
            }}
          >
            ☰
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu" style={{
            backgroundColor: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            padding: '12px 24px',
            textAlign: 'right'
          }}>
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentPage(item.id);
                  setMobileMenuOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '16px 12px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: currentPage === item.id ? '#ef4444' : '#6b7280',
                  textAlign: 'right',
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '64px 24px' }}>
        {/* HOME PAGE */}
        {currentPage === 'home' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '64px' }}>
              <div style={{ marginBottom: '24px' }}>
                <ins className="adsbygoogle"
                     style={{ display: 'block', textAlign: 'center' }}
                     data-ad-client="ca-pub-3720833629798525"
                     data-ad-slot="1234567890"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
                <script>
                  (adsbygoogle = window.adsbygoogle || []).push({});
                </script>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '96px' }}>
              <h2 style={{ fontSize: '48px', fontWeight: '700', margin: '0 0 16px 0', lineHeight: '1.2' }}>
                <span style={{ color: '#ef4444' }}>Convertly</span>
                <span style={{ color: '#111827' }}> – Your Quick Online Utilities</span>
              </h2>
              <p style={{ fontSize: '18px', color: '#6b7280', margin: '0 0 24px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
                Fast, free, repeatable tools for files, text, images, and AI content
              </p>
              <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 32px 0', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
                Convertly is a free online conversion tool platform that helps you convert PDF files to Word documents, humanize AI-generated text with detection scoring, resize images instantly, clean and format text, and much more. No sign-up required, no interruptions, completely free and secure. All conversions happen in your browser - 100% client-side processing means your files never leave your device.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  { page: 'pdf-word', label: 'PDF ↔ Word' },
                  { page: 'text-cleaner', label: 'Text Cleaner' },
                  { page: 'image-resizer', label: 'Image Resizer' },
                  { page: 'ai-humanizer', label: 'AI Humanizer' }
                ].map((btn, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(btn.page)}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tool Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '96px' }}>
              {[
                { title: 'PDF ↔ Word Converter', desc: 'Convert PDF files to editable Word documents and vice versa. Free PDF to Word and Word to PDF conversion online.', icon: FileText, page: 'pdf-word' },
                { title: 'Text Cleaner & Counter', desc: 'Clean up text instantly, count words and characters, remove extra spaces, fix line breaks, and change text case.', icon: Type, page: 'text-cleaner' },
                { title: 'Image Resizer & Converter', desc: 'Resize and convert images to JPG, PNG, or WebP format effortlessly. Adjust dimensions and change image formats instantly.', icon: Image, page: 'image-resizer' },
                { title: 'AI Text Humanizer', desc: 'Bypass AI detection by humanizing AI-generated text. See real-time AI detection score and improve your content naturally.', icon: Zap, page: 'ai-humanizer' }
              ].map((tool, idx) => (
                <div
                  key={idx}
                  onClick={() => setCurrentPage(tool.page)}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '32px',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#f87171';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.1)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <tool.icon style={{ width: '28px', height: '28px', color: '#ef4444' }} />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>{tool.title}</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>{tool.desc}</p>
                </div>
              ))}
            </div>

            {/* Why Choose Convertly */}
            <div style={{ maxWidth: '768px', margin: '48px auto', padding: '32px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
              <h2 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 24px 0', color: '#111827' }}>Why Choose Convertly?</h2>
              
              <div style={{ textAlign: 'left', fontSize: '16px', color: '#6b7280', lineHeight: '1.8' }}>
                <p style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>✨</span>
                  <span><strong style={{ color: '#111827' }}>100% Free</strong> - No hidden costs, no premium plans needed</span>
                </p>
                <p style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>⚡</span>
                  <span><strong style={{ color: '#111827' }}>Instant Processing</strong> - Convert files in seconds</span>
                </p>
                <p style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>🔒</span>
                  <span><strong style={{ color: '#111827' }}>Secure & Private</strong> - All processing happens in your browser, files never leave your device</span>
                </p>
                <p style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>🚀</span>
                  <span><strong style={{ color: '#111827' }}>No Sign-up Required</strong> - Start converting immediately</span>
                </p>
                <p style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>🤖</span>
                  <span><strong style={{ color: '#111827' }}>AI Detection Score</strong> - Real-time detection when humanizing AI text</span>
                </p>
              </div>
            </div>

            {/* FAQ Section */}
            <div style={{ maxWidth: '768px', margin: '48px auto', padding: '32px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
              <h2 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 32px 0', color: '#111827' }}>Frequently Asked Questions</h2>
              
              <div style={{ textAlign: 'left' }}>
                <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 12px 0', color: '#111827' }}>Is my data safe?</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>Yes! All conversions happen directly in your browser using client-side processing. Your files never leave your device and are never sent to our servers. Your privacy is guaranteed.</p>
                </div>

                <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 12px 0', color: '#111827' }}>Why is Convertly completely free?</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>We're supported by ads. There's no premium tier, hidden fees, or subscription model. We believe conversion tools should be accessible to everyone without barriers.</p>
                </div>

                <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 12px 0', color: '#111827' }}>What file formats do you support?</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>We support PDF, Word documents (DOCX), images (PNG, JPG, WebP), and text files. Each tool is optimized for specific formats to ensure the best conversion quality.</p>
                </div>

                <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 12px 0', color: '#111827' }}>How does the AI Humanizer work?</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>The AI Humanizer analyzes your text for common AI indicators (formal transitions, lack of contractions, passive voice) and transforms them into more natural, human-like writing. It also provides a real-time AI detection score.</p>
                </div>

                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 12px 0', color: '#111827' }}>Do you store any of my files?</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>No. All files are processed locally in your browser and deleted immediately after conversion. We don't store, track, or access any of your files.</p>
                </div>
              </div>
            </div>

            {/* Coming Soon */}
            <div style={{ backgroundColor: '#f9fafb', border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '32px', textAlign: 'center', marginTop: '48px' }}>
              <Zap style={{ width: '20px', height: '20px', color: '#ef4444', margin: '0 auto 12px', display: 'block' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>Coming Soon</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>More tools: PDF Summarizer, Document Translator, Image Auto-Crop, and more AI-powered utilities.</p>
            </div>
          </div>
        )}

        {/* PDF TO WORD PAGE */}
        {currentPage === 'pdf-word' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <ins className="adsbygoogle"
                     style={{ display: 'block', textAlign: 'center' }}
                     data-ad-client="ca-pub-3720833629798525"
                     data-ad-slot="2345678901"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
                <script>
                  (adsbygoogle = window.adsbygoogle || []).push({});
                </script>
              </div>
            </div>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <FileText style={{ width: '40px', height: '40px', color: '#ef4444' }} />
            </div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>
              {conversionMode === 'pdf-to-word' ? 'Free PDF to Word Converter - Online PDF to DOC Tool' : 'Free Word to PDF Converter - Convert DOC to PDF Online'}
            </h1>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 48px 0' }}>
              {conversionMode === 'pdf-to-word' 
                ? 'Convert PDF files to editable Word documents instantly. Fast, secure, and completely free. No sign-up required.'
                : 'Convert Word documents to PDF format online. Easy, instant conversion with no software needed.'}
            </p>

            {/* Mode Toggle Switch */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '48px', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: conversionMode === 'pdf-to-word' ? '#ef4444' : '#9ca3af' }}>PDF → Word</span>
              <button
                onClick={() => {
                  setConversionMode(conversionMode === 'pdf-to-word' ? 'word-to-pdf' : 'pdf-to-word');
                  setConvertedFile(null);
                  setErrorMsg('');
                }}
                style={{
                  width: '64px',
                  height: '32px',
                  borderRadius: '16px',
                  border: 'none',
                  backgroundColor: '#e5e7eb',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    transition: 'all 0.3s ease',
                    transform: conversionMode === 'word-to-pdf' ? 'translateX(32px)' : 'translateX(0)'
                  }}
                />
              </button>
              <span style={{ fontSize: '14px', fontWeight: '600', color: conversionMode === 'word-to-pdf' ? '#ef4444' : '#9ca3af' }}>Word → PDF</span>
            </div>

            <div style={{ maxWidth: '480px', margin: '0 auto', backgroundColor: 'white', border: '2px dashed #d1d5db', borderRadius: '12px', padding: '48px 32px' }}>
              <label htmlFor="file-input" style={{ cursor: 'pointer', display: 'block' }}>
                <Upload style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 16px', display: 'block' }} />
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0' }}>
                  Click to upload or drag a {conversionMode === 'pdf-to-word' ? 'PDF' : 'Word'} file
                </p>
                <p style={{ fontSize: '13px', color: '#374151', margin: '16px 0', fontWeight: (conversionMode === 'pdf-to-word' ? pdfFile : wordFile) ? '600' : '400' }}>
                  {(conversionMode === 'pdf-to-word' ? pdfFile : wordFile) ? (conversionMode === 'pdf-to-word' ? pdfFile.name : wordFile.name) : 'No file selected'}
                </p>
              </label>
              <input 
                type="file" 
                accept={conversionMode === 'pdf-to-word' ? '.pdf' : '.doc,.docx'} 
                onChange={(e) => {
                  if (conversionMode === 'pdf-to-word') {
                    setPdfFile(e.target.files[0]);
                  } else {
                    setWordFile(e.target.files[0]);
                  }
                  setConvertedFile(null);
                  setErrorMsg('');
                }}
                id="file-input" 
                style={{ display: 'none' }} 
              />

              {errorMsg && (
                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', backgroundColor: errorMsg.includes('✓') ? '#d1fae5' : '#fee2e2', color: errorMsg.includes('✓') ? '#065f46' : '#991b1b', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <AlertCircle style={{ width: '16px', height: '16px' }} />
                  {errorMsg}
                </div>
              )}

              <button
                onClick={conversionMode === 'pdf-to-word' ? handlePdfToWord : handleWordToPdf}
                disabled={converting}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: converting ? 'not-allowed' : 'pointer',
                  marginTop: '24px',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  transition: 'all 0.2s ease',
                  opacity: converting ? 0.6 : 1
                }}
              >
                {converting ? 'Converting...' : (conversionMode === 'pdf-to-word' ? 'Convert PDF to Word' : 'Convert Word to PDF')}
              </button>

              {convertedFile && (
                <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #dcfce7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Download style={{ width: '20px', height: '20px', color: '#16a34a' }} />
                    <span style={{ fontWeight: '600', color: '#15803d', fontSize: '14px' }}>Ready to download!</span>
                  </div>
                  <button
                    onClick={() => downloadFile(convertedFile)}
                    style={{
                      width: '100%',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Download {convertedFile.name}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TEXT CLEANER PAGE */}
        {currentPage === 'text-cleaner' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <ins className="adsbygoogle"
                     style={{ display: 'block', textAlign: 'center' }}
                     data-ad-client="ca-pub-3720833629798525"
                     data-ad-slot="3456789012"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
                <script>
                  (adsbygoogle = window.adsbygoogle || []).push({});
                </script>
              </div>
            </div>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Type style={{ width: '40px', height: '40px', color: '#ef4444' }} />
            </div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>Online Text Cleaner & Word Counter Tool</h1>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 48px 0' }}>Clean up your text instantly, count words and characters, remove extra spaces, fix line breaks, and change text case for free.</p>

            <div style={{ maxWidth: '768px', margin: '0 auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '32px' }}>
              <textarea
                value={textInput}
                onChange={handleTextChange}
                placeholder="Paste your text here..."
                style={{
                  width: '100%',
                  height: '192px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: "'Inter', sans-serif",
                  boxSizing: 'border-box',
                  resize: 'none',
                  marginBottom: '16px'
                }}
              />

              <div style={{ display: 'flex', gap: '32px', marginBottom: '24px', fontSize: '14px' }}>
                <div><span style={{ fontWeight: '600', color: '#374151' }}>Words: </span><span style={{ color: '#6b7280' }}>{wordCount}</span></div>
                <div><span style={{ fontWeight: '600', color: '#374151' }}>Characters: </span><span style={{ color: '#6b7280' }}>{charCount}</span></div>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px', marginBottom: '24px', textAlign: 'left' }}>
                <label style={{ display: 'flex', gap: '12px', fontSize: '14px', marginBottom: '16px', cursor: 'pointer', alignItems: 'center' }}>
                  <input type="checkbox" checked={removeSpaces} onChange={(e) => setRemoveSpaces(e.target.checked)} style={{ accentColor: '#ef4444', cursor: 'pointer' }} />
                  <span>Remove extra spaces</span>
                </label>
                <label style={{ display: 'flex', gap: '12px', fontSize: '14px', marginBottom: '16px', cursor: 'pointer', alignItems: 'center' }}>
                  <input type="checkbox" checked={fixBreaks} onChange={(e) => setFixBreaks(e.target.checked)} style={{ accentColor: '#ef4444', cursor: 'pointer' }} />
                  <span>Fix line breaks</span>
                </label>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Case Change</label>
                  <select value={caseChange} onChange={(e) => setCaseChange(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>
                    <option>No case change</option>
                    <option>Uppercase</option>
                    <option>Lowercase</option>
                    <option>Capitalize</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleProcessText}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                Process Text
              </button>

              {cleanedText && (
                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3e8e8', borderLeft: '4px solid #ef4444', borderRadius: '8px', padding: '16px', marginTop: '24px', textAlign: 'left' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Cleaned Text:</h4>
                  <p style={{ fontSize: '13px', color: '#4b5563', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{cleanedText}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* IMAGE RESIZER PAGE */}
        {currentPage === 'image-resizer' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <ins className="adsbygoogle"
                     style={{ display: 'block', textAlign: 'center' }}
                     data-ad-client="ca-pub-3720833629798525"
                     data-ad-slot="4567890123"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
                <script>
                  (adsbygoogle = window.adsbygoogle || []).push({});
                </script>
              </div>
            </div>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Image style={{ width: '40px', height: '40px', color: '#ef4444' }} />
            </div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>Free Online Image Resizer & Format Converter</h1>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 48px 0' }}>Resize images and convert to JPG, PNG, or WebP format instantly. Adjust dimensions and change image formats online for free.</p>

            <div style={{ maxWidth: '768px', margin: '0 auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '32px' }}>
              <label htmlFor="image-input" style={{ display: 'block', cursor: 'pointer', border: '2px dashed #d1d5db', borderRadius: '8px', padding: '48px 32px', marginBottom: '24px' }}>
                <Upload style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 16px', display: 'block' }} />
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0' }}>Click to upload JPG, PNG, or WebP</p>
                <p style={{ fontSize: '13px', color: '#374151', margin: '16px 0', fontWeight: imageFile ? '600' : '400' }}>{imageFile ? imageFile.name : 'No file selected'}</p>
              </label>
              <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setImageFile(file);
                  setProcessedImage(null);
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setImagePreview(event.target.result);
                    setTimeout(() => {
                      const imgElement = new window.Image();
                      imgElement.onload = () => {
                        setOriginalImageSize({ width: imgElement.width, height: imgElement.height });
                        setImageWidth(imgElement.width.toString());
                        setImageHeight(imgElement.height.toString());
                      };
                      imgElement.src = event.target.result;
                    }, 0);
                  };
                  reader.readAsDataURL(file);
                }} id="image-input" style={{ display: 'none' }} />

              {imagePreview && (
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                  <img src={imagePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', marginBottom: '12px' }} />
                  {originalImageSize && (
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: '0' }}>
                      Original: {originalImageSize.width}×{originalImageSize.height}px
                    </p>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Width (px)</label>
                  <input type="number" value={imageWidth} onChange={(e) => handleWidthChange(e.target.value)} placeholder="800" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Height (px)</label>
                  <input type="number" value={imageHeight} onChange={(e) => handleHeightChange(e.target.value)} placeholder="600" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: '24px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setKeepAspectRatio(!keepAspectRatio)}>
                <input 
                  type="checkbox" 
                  checked={keepAspectRatio} 
                  onChange={(e) => setKeepAspectRatio(e.target.checked)}
                  style={{ accentColor: '#ef4444', cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer', margin: 0 }}>
                  {keepAspectRatio ? '🔒 Lock Aspect Ratio' : '🔓 Unlock Aspect Ratio'}
                </label>
              </div>

              <div style={{ marginBottom: '24px', textAlign: 'left' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Output Format</label>
                <select value={imageFormat} onChange={(e) => setImageFormat(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}>
                  <option>PNG</option>
                  <option>JPG</option>
                  <option>WebP</option>
                </select>
              </div>

              {errorMsg && (
                <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', backgroundColor: errorMsg.includes('✓') ? '#d1fae5' : '#fee2e2', color: errorMsg.includes('✓') ? '#065f46' : '#991b1b', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <AlertCircle style={{ width: '16px', height: '16px' }} />
                  {errorMsg}
                </div>
              )}

              <button
                onClick={handleImageResize}
                disabled={processingImage}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: processingImage ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  transition: 'all 0.2s ease',
                  opacity: processingImage ? 0.6 : 1,
                  marginBottom: '16px'
                }}
              >
                {processingImage ? 'Processing...' : 'Convert / Resize'}
              </button>

              {processedImage && (
                <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #dcfce7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Download style={{ width: '20px', height: '20px', color: '#16a34a' }} />
                    <span style={{ fontWeight: '600', color: '#15803d', fontSize: '14px' }}>Image ready!</span>
                  </div>
                  <img 
                    src={processedImage.url} 
                    alt="processed" 
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '6px', marginBottom: '12px', maxHeight: '200px' }}
                  />
                  <button
                    onClick={() => downloadFile(processedImage)}
                    style={{
                      width: '100%',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Download {processedImage.name}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI HUMANIZER PAGE */}
        {currentPage === 'ai-humanizer' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <ins className="adsbygoogle"
                     style={{ display: 'block', textAlign: 'center' }}
                     data-ad-client="ca-pub-3720833629798525"
                     data-ad-slot="5678901234"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
                <script>
                  (adsbygoogle = window.adsbygoogle || []).push({});
                </script>
              </div>
            </div>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Type style={{ width: '40px', height: '40px', color: '#ef4444' }} />
            </div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>AI Text Humanizer</h1>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 48px 0' }}>Transform AI-generated text to sound more natural and human-like. Perfect for making ChatGPT, Claude, and other AI content more engaging.</p>

            <div style={{ maxWidth: '768px', margin: '0 auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '32px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', textAlign: 'left' }}>Paste your AI-generated text here:</label>
              <textarea
                value={aiText}
                onChange={(e) => {
                  setAiText(e.target.value);
                  setHumanizedText('');
                }}
                placeholder="Paste AI-generated text here and we'll make it sound more human..."
                style={{
                  width: '100%',
                  height: '192px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: "'Inter', sans-serif",
                  boxSizing: 'border-box',
                  resize: 'none',
                  marginBottom: '24px'
                }}
              />

              {aiText && (
                <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#92400e', textTransform: 'uppercase' }}>🤖 AI Detection Score</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1, backgroundColor: '#fecaca', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                      <div 
                        style={{ 
                          width: `${calculateAIScore(aiText)}%`, 
                          height: '100%', 
                          backgroundColor: calculateAIScore(aiText) > 70 ? '#ef4444' : calculateAIScore(aiText) > 40 ? '#f97316' : '#22c55e',
                          transition: 'width 0.3s ease'
                        }} 
                      />
                    </div>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: calculateAIScore(aiText) > 70 ? '#991b1b' : calculateAIScore(aiText) > 40 ? '#92400e' : '#15803d', minWidth: '50px' }}>
                      {calculateAIScore(aiText)}%
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#92400e', margin: '8px 0 0 0' }}>
                    {calculateAIScore(aiText) > 70 ? '🔴 Highly AI-generated' : calculateAIScore(aiText) > 40 ? '🟠 Likely AI-generated' : '🟢 Mostly human-like'}
                  </p>
                </div>
              )}

              {errorMsg && (
                <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', backgroundColor: errorMsg.includes('✓') ? '#d1fae5' : '#fee2e2', color: errorMsg.includes('✓') ? '#065f46' : '#991b1b', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <AlertCircle style={{ width: '16px', height: '16px' }} />
                  {errorMsg}
                </div>
              )}

              <button
                onClick={handleHumanizeText}
                disabled={humanizingText}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: humanizingText ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  transition: 'all 0.2s ease',
                  opacity: humanizingText ? 0.6 : 1,
                  marginBottom: '16px'
                }}
              >
                {humanizingText ? 'Humanizing...' : 'Humanize Text'}
              </button>

              {humanizedText && (
                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3e8e8', borderLeft: '4px solid #ef4444', borderRadius: '8px', padding: '16px', marginTop: '24px', textAlign: 'left' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Humanized Text:</h4>
                  <p style={{ fontSize: '13px', color: '#4b5563', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{humanizedText}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(humanizedText);
                      setErrorMsg('✓ Copied to clipboard!');
                    }}
                    style={{
                      marginTop: '12px',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Copy to Clipboard
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid #e5e7eb', backgroundColor: 'white', marginTop: '96px', padding: '24px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
        © 2026 Convertly. Free online utilities for everyone.
      </footer>
    </div>
  );
}